import { sendMeasurement } from 'core/messaging/measurement/measurement';
import { buildMeasurement } from 'core/service/measurements/measurementUtils';
import { logInfo, logWarn } from 'core-framework/util/LogMessage';
import { TopDestinationsResponse, UseQueryStateBaseResult } from 'store/api/api.types';
import { SuggestionResult } from 'core/type/SuggestionResult.types';
import { convertError } from 'store/api/apiUtils';
import { MeasurementName } from 'core/constant/MeasurementName';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { IMeasurementTags } from '@agoda/messaging-client';
import { ActivitiesPageTypes } from 'core/constant/analytics/AnalyticsPageType';
import { MeasurementStatus } from 'core/constant/MeasurementStatus';
import { FieldValue } from 'core/constant/FieldValue';

enum ApiNames {
    getSuggestResult,
    getTopDestination,
}

const sendHomePageMeasurements = (
    apiName: ApiNames,
    message: string,
    measurementName: MeasurementName,
    pageTypeId: ActivitiesPageTypes,
    config: BootstrapConfig,
    tags: IMeasurementTags,
    searchText?: string,
): boolean => {
    const measurement = buildMeasurement(ApiNames[apiName], measurementName, pageTypeId, message, 1, false, config);
    measurement.tags = {
        ...measurement.tags,
        ...tags,
        searchText: searchText || FieldValue.Undefined,
    };

    sendMeasurement({ ...measurement, name: MeasurementName.ApiTextSearchStatus }, pageTypeId);

    if (
        measurementName === MeasurementName.ApiTextSearchSuccess ||
        measurementName === MeasurementName.ApiTextSearchNoResult ||
        measurementName === MeasurementName.ApiTopDestinationSuccess ||
        measurementName === MeasurementName.ApiTopDestinationNoResult
    ) {
        logInfo(message, pageTypeId, measurement.tags);
        return true;
    }
    logWarn(message, pageTypeId, measurement.tags);
    return true;
};

export const measureTextSearchApiResponse = (
    response: UseQueryStateBaseResult<{ suggestionList: SuggestionResult[] }>,
    searchText: string,
    pageTypeId: number,
    config: BootstrapConfig,
): boolean => {
    const error = convertError(response.error) || { status: FieldValue.Undefined, message: FieldValue.Undefined };
    const messageLowerCase = error.message.toLowerCase();
    const measurementTags: IMeasurementTags = {
        statusCode: error.status.toString(),
    };
    if (response.isSuccess) {
        const numberOfResults = response.data?.suggestionList?.length ?? 0;
        measurementTags.status = MeasurementStatus.Success;
        if (numberOfResults === 0) {
            return sendHomePageMeasurements(ApiNames.getSuggestResult, 'zero results', MeasurementName.ApiTextSearchNoResult, pageTypeId, config, measurementTags, searchText);
        }
        return sendHomePageMeasurements(ApiNames.getSuggestResult, 'success', MeasurementName.ApiTextSearchSuccess, pageTypeId, config, measurementTags, searchText);
    }

    measurementTags.status = MeasurementStatus.Failed;
    return sendHomePageMeasurements(ApiNames.getSuggestResult, messageLowerCase, MeasurementName.ApiTextSearchError, pageTypeId, config, measurementTags, searchText);
};

export const measureLanguageNotSupported = (locale: string, pageTypeId: number, config: BootstrapConfig): boolean => {
    const measurement = buildMeasurement('getSupportedLanguage', MeasurementName.LanguageNotSupported, pageTypeId, '', 1, false, config);
    measurement.tags.locale = locale;

    sendMeasurement(measurement, pageTypeId);
    return true;
};

export const measureTopDestinationApiResponse = (response: TopDestinationsResponse, pageTypeId: ActivitiesPageTypes, config: BootstrapConfig): boolean => {
    const error = convertError(response.error) || { status: FieldValue.Undefined, message: FieldValue.Undefined };
    const messageLowerCase = error.message.toLowerCase();
    const measurementTags: IMeasurementTags = {
        statusCode: error.status.toString(),
    };
    if (response.isSuccess) {
        const numberOfResults = response.data?.topDomesticDestinations?.length ?? 0;
        measurementTags.status = MeasurementStatus.Success;
        if (numberOfResults === 0) {
            return sendHomePageMeasurements(ApiNames.getTopDestination, 'zero results', MeasurementName.ApiTopDestinationNoResult, pageTypeId, config, measurementTags);
        }
        return sendHomePageMeasurements(ApiNames.getTopDestination, 'success', MeasurementName.ApiTopDestinationSuccess, pageTypeId, config, measurementTags);
    }
    measurementTags.status = MeasurementStatus.Failed;
    return sendHomePageMeasurements(ApiNames.getTopDestination, messageLowerCase, MeasurementName.ApiTopDestinationError, pageTypeId, config, measurementTags, undefined);
};
